import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
    Badge,
    Navbar,
    NavbarBrand,
    Button,
    Spinner,
    Container,
    UncontrolledDropdown,
    UncontrolledButtonDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Offcanvas,
    OffcanvasBody,
} from 'reactstrap';
import cls from 'classnames';
import { isEmpty } from 'lodash-es';
import { v7 as uuid } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAt,
    faBars, faCircleUser, faEdit, faHeadset, faPlus, faSearch, faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons';
import { faCompass } from '@fortawesome/free-regular-svg-icons';
import { Link } from '@s20/components/common/Link';
import { getPermissionStrings, getUserHasSelfDispatchAccess } from '@s20/selectors/features/permissions.selectors';
import {
    getIsAuthenticated, getPageTitle, getTenantSettings, getUser,
    getVersion, getTimesheetNotifications, getTenant, getUserShortcuts,
} from '@s20/selectors/features/common.selectors';
import { setUser, upsertUserShortcut } from '@s20/slices/features/common.slice';
import i18n from '@s20/i18n';
import { Hide } from '@s20/components/common/Hide';
import { httpClient } from '@s20/services/httpClient';
import { Can } from '../Can';
import { ApplicationPaths } from '../api-authorization/ApiAuthorizationConstants';
import CommonContext, {
    ApplicationPermissions,
    ApiRoutes,

    AppNavPaths,
} from '../Common';
import {
    FlexColumnStart,
    FlexEndRow,
    FlexStartRow,
    FormLabel,
    IconButton,
    Input,
    onFieldChange,
    SmallOutlineButton,
    toasty,
} from '../common/forms/FormElements';
import { AccordionCollapse } from '../common/AccordionCollapse';
import QuickSearch from '../QuickSearch';
import { Alert, Modal } from '../common/Modal';
import { TipAlert } from '../common/TipAlert';
import TenantAccessModal from './TenantAccessModal';
import { FlattenedSections, Requirements, Sections } from './constants';
import { NavMenuItem } from './NavMenuItem';
import { QuickLinks } from './QuickLinks';
import { QuickJob } from './QuickJob';
import { QuickReturn } from './QuickReturn';
import { ChangeLanguage } from './ChangeLanguage';
import { DispatchPlusNotificationSubscription } from './DispatchPlusNotificationSubscription';
import './index.scss';

//Actions that require admins.
const adminEntitiesActions = [
    ApplicationPermissions.chargetype_view,
    ApplicationPermissions.company_view,
    ApplicationPermissions.chargetypeunit_view,
    ApplicationPermissions.contractstatus_view,
    ApplicationPermissions.contractTemplate_view,
    ApplicationPermissions.employeeaccountstatus_view,
    ApplicationPermissions.equipmenttype_view,
    ApplicationPermissions.holiday_view,
    ApplicationPermissions.equipmentstatus_view,
];

const handleOpenTenantModal = () => {
    Alert.show({
        title: 'Change Tenant',
        body: <TenantAccessModal />,
    });
};

class NavMenu extends Component {
    static displayName = NavMenu.name;
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            collapseId: '',
            loading: false,
            editingQuickLinks: false,
            open: false,
            navbarSearchText: '',
            showQuickJobForm: false,
            showQuickReturnForm: false,
            isQuickRental: false,
        };

        this.inputRef = React.createRef();

        this.onLogoutClicked = this.onLogoutClicked.bind(this);
        this.renderPage = this.renderPage.bind(this);
        this.onQuickLinksEditButtonClick = this.onQuickLinksEditButtonClick.bind(this);
        this.onChange = this.onChange.bind(this);
        this.toggleQuickJobSlideout = this.toggleQuickJobSlideout.bind(this);
    }

    toggleMenu = (event) => {
        const { open } = this.state;

        event?.preventDefault();
        event?.stopPropagation();

        this.setState({
            open: !open,
            navbarSearchText: '',
            editingQuickLinks: false,
            collapseId: '',
        });

        if (!open) {
            // Hacky - TODO: Don't do this?
            setTimeout(() => this.inputRef.current?.focus(), 50);
        }
    };

    toggleMenuClosed = () => {
        this.setState({
            editingQuickLinks: false, open: false, navbarSearchText: '', collapseId: '',
        });
    };

    toggleCollapse = (collapseId) => () => {
        this.setState((prevState) => ({
            collapseId: prevState.collapseId !== collapseId ? collapseId : '',
        }));
    };

    onChangeEmailButtonClick = () => {
        const { user } = this.context;
        const { dispatch } = this.props;

        Modal.show({
            title: 'Change Email Address',
            onSubmit: async ({ email }) => {
                try {
                    await httpClient.put(
                        ApiRoutes.auth.updateEmail(),
                        { email },
                    );

                    dispatch(setUser({
                        ...user,
                        email,
                    }));

                    toasty.success('Email address changed successfully');
                } catch (e) {
                    console.error({ e });
                    toasty.error('There was an error updating your email address');
                }
            },
            body: () => (
                <>
                    <TipAlert
                        label="Changing your email address will change where notifications are sent as well as the username you use to log in."
                    />

                    <Input
                        label="New Email"
                        name="email"
                        type="email"
                        placeholder={user.email}
                        required
                        error="You must enter a new email address"
                    />
                </>
            ),
        });
    };

    onQuickLinksEditButtonClick = () => {
        const { editingQuickLinks } = this.state;

        this.setState({ editingQuickLinks: !editingQuickLinks });
    };

    onNavLinkClicked = async (e, name = null) => {
        this.toggleMenuClosed();

        if (name && typeof name === 'string') {
            const { dispatch } = this.props;

            const response = await httpClient.post(
                ApiRoutes.shortcut.create(),
                {
                    id: uuid(),
                    name,
                },
            );

            if (response) {
                dispatch(upsertUserShortcut(response));
            }
        }
    };

    onChange = onFieldChange;

    getFilteredSections = () => {
        const { checkUserHasPermission } = this.context;
        const { navbarSearchText } = this.state;

        if (!navbarSearchText) {
            return Sections;
        }

        return Sections.reduce((results, section) => {
            const pages = section.pages.filter((page) =>
                (
                    page.name.toLowerCase().indexOf(navbarSearchText.toLowerCase()) !== -1
                    || page.label.toLowerCase().indexOf(navbarSearchText.toLowerCase()) !== -1
                )
                && checkUserHasPermission(`${page.entity}.${page.action}`)
                && this.validateRequirements(page.requirements || []),
            );

            if (pages.length) {
                return [
                    ...results,
                    {
                        ...section,
                        pages,
                    },
                ];
            }

            return results;
        }, []);
    };

    getShortcuts = () => {
        const { shortcuts } = this.props;
        const { navbarSearchText } = this.state;

        if (!navbarSearchText) {
            return shortcuts;
        }

        return shortcuts.filter((shortcut) => {
            const page = FlattenedSections.find((section) => section.name === shortcut.name);

            if (!page) {
                return false;
            }

            return page.name.toLowerCase().indexOf(navbarSearchText.toLowerCase()) !== -1
                || page.label.toLowerCase().indexOf(navbarSearchText.toLowerCase()) !== -1;
        });
    };

    onLogoutClicked = (event) => {
        event.preventDefault();
        this.toggleMenuClosed();
        const logoutPath = {
            pathname: `${ApplicationPaths.LogOut}`,
            state: { local: true },
        };
        this.context.history.push(logoutPath);
    };

    onClickImpersonation = async () => {
        this.setState({ loading: true });

        try {
            await httpClient.put(
                ApiRoutes.employee.toggleImpersonation(),
                { EmployeeId: null },
                httpClient.format.none,
            );

            window.sessionStorage.removeItem('impersonating');
            window.location.href = '/';
        } catch (error) {
            console.warn(error);
        } finally {
            this.setState({ loading: false });
        }
    };

    renderMenuButton() {
        return (
            <IconButton
                data-trigger="offCanvasMenu"
                className="offCanvasMenuButton btn p-0 m-0"
                onClick={this.toggleMenu}
                size="lg"
            >
                <FontAwesomeIcon
                    size="lg"
                    icon={faBars}
                />
            </IconButton>
        );
    }

    toggleQuickJobSlideout = () => {
        this.setState((prev) => ({
            showQuickJobForm: !prev.showQuickJobForm,
            isQuickRental: prev.showQuickJobForm ? false : prev.isQuickRental,
        }));
    };

    userHasAdminFunction = () => this.props.permissions.some((p) => adminEntitiesActions.indexOf(p) >= 0);

    validateRequirements = (requirements = []) => {
        if (!isEmpty(requirements)) {
            const { hasSelfDispatchingAccess } = this.props;
            const { checkUserHasPermission, tenant } = this.context;

            const { tenantSettings } = tenant;

            for (const requirement of requirements) {
                switch (requirement) {
                    case Requirements.NonFlaggingAccess: {
                        if (!hasSelfDispatchingAccess) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Contracts: {
                        if (!checkUserHasPermission(ApplicationPermissions.contract_view)) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Organizations: {
                        if (!checkUserHasPermission(ApplicationPermissions.location_view)) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Delegates: {
                        if (!checkUserHasPermission(ApplicationPermissions.employee_view) || !checkUserHasPermission(ApplicationPermissions.multi_tenant_all)) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Equipment: {
                        if (!checkUserHasPermission(ApplicationPermissions.equipment_view)) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Employees: {
                        if (!checkUserHasPermission(ApplicationPermissions.employee_view)) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Jobs: {
                        if (
                            !checkUserHasPermission(ApplicationPermissions.job_view)
                            && !checkUserHasPermission(ApplicationPermissions.job_daily_view)
                            && !checkUserHasPermission(ApplicationPermissions.dispatch_view)
                            && !checkUserHasPermission(ApplicationPermissions.dispatch_review_view)
                        ) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Projects: {
                        const projectsEnabled = tenantSettings.useContractProjects === 2 || (
                            tenantSettings.useContractProjects === 1
                            && tenantSettings.allowRentalChargeTypes
                        );

                        if (!projectsEnabled) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Estimates: {
                        if (!tenantSettings.enableEstimate) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Contacts: {
                        if (!tenantSettings.contactScreenEnabled) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.ContactActivities: {
                        if (!tenantSettings.contactActivitiesEnabled) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Payroll: {
                        if (!tenantSettings.payrollEnabled || !checkUserHasPermission(ApplicationPermissions.payroll_view)) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Billing: {
                        if (!tenantSettings.billingEnabled || !checkUserHasPermission(ApplicationPermissions.billing_view)) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Invoicing: {
                        if (!tenantSettings.invoicingEnabled || !checkUserHasPermission(ApplicationPermissions.invoice_view)) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Rentals: {
                        if (
                            !tenantSettings.allowRentalChargeTypes
                            || tenantSettings.combineRentalNonRentalChargesOnRentalInvoices
                            || !checkUserHasPermission(ApplicationPermissions.rental_invoice_view)
                        ) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.ChargeTypeGroups: {
                        if (!tenantSettings.chargeTypeGroupsEnabled) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.PrevailingWage: {
                        const paySchedulesBlocked = !!tenantSettings.enablePrevailingWageContracts
                            && !!tenantSettings.enablePrevailingWageCountyGroups;

                        if (!tenantSettings.enablePrevailingWageContracts || paySchedulesBlocked) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Union: {
                        if (!tenantSettings.enforceContractUnionWork) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.JotForms: {
                        if (!tenantSettings.enablePreJobForm) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.Certifications: {
                        if (!tenantSettings.enableCertification) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.LongTermStatuses: {
                        if (!tenantSettings.employeeLongTermStatusManagementEnabled) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.DailyAttendanceEvents: {
                        if (!tenantSettings.attendanceEventsEnabled) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.QuickSales: {
                        if (!tenantSettings.enableQuickSales || !checkUserHasPermission(ApplicationPermissions.quickSale_view)) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.NonCombinedInvoices: {
                        if (
                            tenantSettings.combineRentalNonRentalChargesOnRentalInvoices
                        ) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.TimeTracker: {
                        if (!tenantSettings.enableContractTimesheetCheckins) {
                            return false;
                        }

                        break;
                    }
                    case Requirements.UnionOrPrevailingWage: {
                        const isUnion = !!tenantSettings.enforceContractUnionWork;
                        const isPWUSCountyGroups = !!tenantSettings.enablePrevailingWageContracts
                            && !!tenantSettings.enablePrevailingWageCountyGroups;

                        if (!isUnion && !isPWUSCountyGroups) {
                            return false;
                        }

                        break;
                    }
                    default: {
                        return false;
                    }
                }
            }
        }

        return true;
    };

    renderPage = (page, editing = false, draggableProps = null) => {
        const {
            notifications: timesheetNotificationCount,
        } = this.props;

        if (!page || !this.validateRequirements(page.requirements)) {
            return null;
        }

        const label = page.i18nKey ? i18n.t(page.i18nKey) : page.label;
        return (
            <NavMenuItem
                key={page.name}
                name={page.name}
                action={page.action}
                entity={page.entity}
                onNavLinkClicked={this.onNavLinkClicked}
                label={label}
                icon={page.icon}
                newTitle={page.newTitle}
                newUrl={page.newUrl}
                searchTitle={label}
                searchUrl={page.url}
                DraggableProps={draggableProps}
                editing={editing}
                notificationCount={page.badge ? page.badge({
                    timesheetNotificationCount, // Add any other badge counts as needed to this list
                }) : undefined}
            />
        );
    };

    renderMenu = () => {
        const { version } = this.props;
        const shortcuts = this.getShortcuts();

        return (
            <Offcanvas
                isOpen={this.state.open}
                toggle={this.toggleMenuClosed}
                direction="start"
                id="offCanvasMenu"
                className="site-menu-offcanvas"
            >
                <header className="site-offcanvas-header p-0 m-0 border-bottom">
                    <div className="d-flex flex-row flex-nowrap align-items-center justify-content-between">
                        {this.renderMenuButton()}

                        <Hide mdUp>
                            <Input
                                placeholder="Search menu"
                                type="search"
                                name="navbarSearchText"
                                value={this.state.navbarSearchText ?? ''}
                                onChange={this.onChange}
                                innerRef={this.inputRef}
                                startAddonClassName="addon-centered"
                                startAddon={(
                                    <FontAwesomeIcon
                                        icon={faSearch}
                                        className="text-muted"
                                    />
                                )}
                            />
                        </Hide>

                        <Hide smDown>
                            <div>
                                <Can
                                    I="view"
                                    a="supporttool"
                                >
                                    <Link
                                        to={AppNavPaths.SupportTools}
                                        title="Support Tools"
                                        component={IconButton}
                                        size="xl"
                                        className="mr-2"
                                    >
                                        <FontAwesomeIcon
                                            icon={faHeadset}
                                        />
                                    </Link>
                                </Can>

                                <Can
                                    I="all"
                                    a="multi_tenant"
                                >
                                    <Button
                                        onClick={handleOpenTenantModal}
                                        color="light"
                                        className="mr-2"
                                    >
                                        Change Tenant
                                    </Button>
                                </Can>
                            </div>
                        </Hide>
                    </div>
                </header>

                <OffcanvasBody className="p-0">
                    <Container className="d-block">
                        {shortcuts.length > 0 && (
                            <>
                                <FlexStartRow className="mb-1">
                                    <FormLabel
                                        label="Quick Links"
                                        className="mr-2"
                                    />

                                    <IconButton
                                        onClick={this.onQuickLinksEditButtonClick}
                                        title="Edit Quick Links"
                                    >
                                        <FontAwesomeIcon
                                            icon={faEdit}
                                        />
                                    </IconButton>
                                </FlexStartRow>

                                <QuickLinks
                                    shortcuts={shortcuts}
                                    renderPage={this.renderPage}
                                    editing={this.state.editingQuickLinks}
                                />

                                <hr />
                            </>
                        )}

                        {this.getFilteredSections().map((section) => {
                            if (!this.validateRequirements(section.requirements)) {
                                return null;
                            }

                            const sectionTitle = section.i18nKey ? i18n.t(section.i18nKey) : section.title;
                            return (
                                <AccordionCollapse
                                    key={`${section.title}${!!this.state.navbarSearchText}`}
                                    title={sectionTitle}
                                    defaultOpen={!!this.state.navbarSearchText}
                                >
                                    {section.pages.map((page) => this.renderPage(page, false))}
                                </AccordionCollapse>
                            );
                        })}

                        <hr />

                        {Boolean(version) && (
                            <div className="text-muted mb-3">
                                v.{version}
                            </div>
                        )}

                        <Button
                            color="link"
                            onClick={this.onLogoutClicked}
                            title="Logout"
                            className="text-danger p-0"
                        >
                            <FontAwesomeIcon
                                icon={faSignOutAlt}
                                className="mr-2"
                            />

                            Log Out
                        </Button>
                    </Container>
                </OffcanvasBody>
            </Offcanvas>
        );
    };

    render() {
        const {
            isAuthenticated, tenant, user, permissions, tenantSettings,
        } = this.props;

        const { loading } = { ...this.state };

        let showBrandLabel = tenant?.tenantSettings?.defaultTheme?.showBrandLabel ?? false;
        if (!showBrandLabel && this.context.checkUserHasPermission) {
            showBrandLabel = this.context.checkUserHasPermission(ApplicationPermissions.multi_tenant_all);
        }

        document.title = tenant?.companyName ?? '';

        const hasCustomerPortal = this.context.checkUserHasPermission(ApplicationPermissions.customerportal_all);
        const hasQuickRentalPermission = this.context.checkUserHasPermission(ApplicationPermissions.quick_rental_edit);

        return (
            <Navbar
                id="siteHeader"
                className={cls('navbar-expand-sm navbar-toggleable-sm ng-white border-bottom m-0 pt-0 pb-0', {
                    impersonating: user?.isImpersonating,
                })}
                light
            >
                {!!isAuthenticated &&
                    !!user &&
                    !!permissions &&
                    !hasCustomerPortal && (
                        <div className="menu-button-wrapper">
                            {this.renderMenuButton()}
                        </div>
                    )}

                <FlexStartRow>
                    <NavbarBrand
                        tag={Link}
                        to="/"
                    >
                        <img
                            src={ApiRoutes.tenant.getTenantLogo(tenant?.id)}
                            alt="Logo"
                            loading="eager"
                        />

                        {!!showBrandLabel && (
                            <Hide mdUp>
                                <span className="brand-label">
                                    {tenant?.companyName ?? ''}
                                </span>
                            </Hide>
                        )}
                    </NavbarBrand>

                    {Boolean(user) && (
                        <Can
                            I="all"
                            a="customer_portal"
                            not
                        >
                            <QuickSearch />
                        </Can>
                    )}

                    <Hide mdUp>
                        {Boolean(tenantSettings?.bannerMessage) && (
                            <h2>
                                <Badge
                                    color="danger"
                                    fade="false"
                                >
                                    {tenantSettings.bannerMessage}
                                </Badge>
                            </h2>
                        )}
                    </Hide>

                </FlexStartRow>

                {user?.isImpersonating && (
                    <Button
                        color="primary"
                        onClick={this.onClickImpersonation}
                        outline
                        className="mr-2"
                        disabled={loading}
                    >
                        {Boolean(loading) && (
                            <Spinner
                                size="sm"
                                className="mr-1"
                            />
                        )}

                        Stop Impersonating

                        <Hide mdUp>
                            {' '}
                            {user?.firstName}
                        </Hide>
                    </Button>
                )}

                {!!user && (
                    <FlexEndRow>
                        <Hide mdUp>
                            <Can
                                I="view"
                                a="supporttool"
                            >
                                <Link
                                    to={AppNavPaths.SupportTools}
                                    title="Support Tools"
                                    component={IconButton}
                                    size="xl"
                                    className="mr-2"
                                >
                                    <FontAwesomeIcon
                                        icon={faHeadset}
                                    />
                                </Link>
                            </Can>
                        </Hide>

                        {!user.isImpersonating && (
                            <Can
                                I="create"
                                a="quick_job"
                            >
                                {tenantSettings?.allowRentalChargeTypes && tenantSettings?.quickRentalContractTemplateId && hasQuickRentalPermission ? (
                                    <UncontrolledButtonDropdown className="mr-2">
                                        <SmallOutlineButton
                                            onClick={() => this.setState({ showQuickJobForm: true, isQuickRental: false })}
                                        >
                                            <FontAwesomeIcon
                                                icon={faPlus}
                                                className="mr-2"
                                            />
                                            New Job
                                        </SmallOutlineButton>
                                        <DropdownToggle
                                            color="outline-primary"
                                            size="sm"
                                            caret
                                            split
                                        />
                                        <DropdownMenu>
                                            <DropdownItem onClick={() => this.setState({ showQuickJobForm: true, isQuickRental: false })}>
                                                New Job
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={() => this.setState({ showQuickJobForm: true, isQuickRental: true })}
                                            >
                                                New Quick Rental
                                            </DropdownItem>
                                            <DropdownItem
                                                onClick={() => this.setState({ showQuickReturnForm: true })}
                                            >
                                                Quick Return
                                            </DropdownItem>
                                        </DropdownMenu>
                                    </UncontrolledButtonDropdown>
                                ) : (
                                    <SmallOutlineButton
                                        className="mr-2"
                                        onClick={() => this.setState({ showQuickJobForm: true, isQuickRental: false })}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPlus}
                                            className="mr-2"
                                        />
                                        New Job
                                    </SmallOutlineButton>
                                )}

                                <QuickJob
                                    show={this.state.showQuickJobForm}
                                    onClose={this.toggleQuickJobSlideout}
                                    isQuickRental={this.state.isQuickRental}
                                />

                                <QuickReturn
                                    show={this.state.showQuickReturnForm}
                                    onClose={() => this.setState({ showQuickReturnForm: false })}
                                />
                            </Can>
                        )}

                        <UncontrolledDropdown>
                            <DropdownToggle
                                tag="span"
                                data-toggle="dropdown"
                                role="menu"
                            >
                                <FlexColumnStart
                                    component={Button}
                                    color="link"
                                    className="align-items-center p-2 tenant-icon"
                                >
                                    <FontAwesomeIcon
                                        size="2x"
                                        icon={faCircleUser}
                                    />

                                    <small>
                                        {user?.firstName}
                                    </small>
                                </FlexColumnStart>
                            </DropdownToggle>

                            <DropdownMenu
                                container="body"
                                end
                            >
                                <Can
                                    I="all"
                                    a="customer_portal"
                                >
                                    <DropdownItem onClick={this.onChangeEmailButtonClick}>
                                        <FontAwesomeIcon
                                            icon={faAt}
                                        />

                                        Change Email
                                    </DropdownItem>
                                </Can>

                                <Can
                                    I="all"
                                    a="multi_tenant"
                                >
                                    <DropdownItem
                                        onClick={handleOpenTenantModal}
                                    >
                                        <FontAwesomeIcon
                                            icon={faCompass}
                                        />

                                        Change Tenant
                                    </DropdownItem>
                                </Can>

                                <ChangeLanguage />

                                <DispatchPlusNotificationSubscription />

                                <DropdownItem
                                    onClick={this.onLogoutClicked}
                                    className="text-danger"
                                >
                                    <FontAwesomeIcon
                                        icon={faSignOutAlt}
                                    />

                                    Log Out
                                </DropdownItem>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    </FlexEndRow>
                )}

                {!!user &&
                    !!permissions &&
                    !!tenantSettings &&
                    this.renderMenu()}
            </Navbar>
        );
    }
}

const mapStateToProps = (state) => ({
    isAuthenticated: getIsAuthenticated(state),
    permissions: getPermissionStrings(state),
    tenantSettings: getTenantSettings(state),
    tenant: getTenant(state),
    version: getVersion(state),
    user: getUser(state),
    pageTitle: getPageTitle(state),
    hasSelfDispatchingAccess: getUserHasSelfDispatchAccess(state),
    notifications: getTimesheetNotifications(state),
    shortcuts: getUserShortcuts(state),
});

export default connect(mapStateToProps)(NavMenu);
